/**
 * One order-sheet sheet for a Market making single order (Get out, Move bid, the custom hedge):
 * opened on the position + venue with the opportunity id (order-sheet 0.1.2), so the order joins
 * the opportunity. The sheet sizes, prices, fees and preflights the order; this hook adds the
 * compact confirm's send:
 *
 *   1. note the numbers on screen (`sheetFigures` of the sheet's quote)
 *   2. `prepare` (the caller re-aims the sheet: a new best venue, a re-planned bid)
 *   3. re-read the position (`refresh`) and, when the inputs changed, the exact preflight
 *   4. moved since step 1 → nothing is sent: `moved`, and the caller asks to confirm again
 *   5. blocked → `blocked` with greenbook's copy for the sheet's issues (ticketModel `issueText`)
 *   6. Real: `place()` answers `confirm_required` (the SDK's own confirm step; the compact
 *      confirm the user just pressed is that confirmation), then `beforeSend` (cancel-first),
 *      then `confirm()`, which sends only if nothing changed since `place()`. Paper:
 *      `beforeSend`, then `place()`.
 *   An unknown outcome (no answer) leaves the sheet in `unknown`: the next submit re-asks with
 *   the same idempotency key (never a second order) and skips the cancel-first.
 *
 * `arm` (the custom hedge's two-step Real review): steps 1–5, then `place()` → `armed`. The
 * next submit is the Confirm, sent with `confirm()` as above.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createInertSheet, isOrderSheetError, userMessageFor, type OrderSheetClient, type PlaceResult, type PositionRef, type Sheet, type SheetInit, type SheetState } from "@openmarketsai/order-sheet";
import { useOrderSheet, type OrderSheetHandle } from "@openmarketsai/order-sheet/react";
import { figuresMoved, sheetFigures, type SheetFigures } from "../../lib/marketMaking.js";
import { issueText } from "../../lib/ticketModel.js";
import { useOddsFormat } from "../../lib/odds.js";

export type SubmitOutcome =
  | { status: "placed"; result: PlaceResult; figures: SheetFigures | null; state: SheetState }
  /** Nothing sent: the numbers changed since the user saw them (`to` is what's on screen now). */
  | { status: "moved"; from: SheetFigures | null; to: SheetFigures | null }
  /** `arm`: the Real confirmation is set; the next submit sends. */
  | { status: "armed"; figures: SheetFigures }
  | { status: "blocked"; message: string }
  /** Thrown. `unknown`: no answer, the order may exist (the next submit re-asks, same key). */
  | { status: "error"; message: string; unknown: boolean };

export interface MmSheet {
  h: OrderSheetHandle;
  /** The sheet's numbers now (null until it has a quote). */
  figures: SheetFigures | null;
  /** The sheet is still loading, or its exact preflight hasn't answered yet. */
  checking: boolean;
  /** What's in flight: re-checking the numbers, or sending. */
  step: "checking" | "placing" | null;
  submit: (o?: { prepare?: (sheet: Sheet) => void; beforeSend?: () => Promise<void>; arm?: boolean }) => Promise<SubmitOutcome>;
}

/**
 * `ref` and `init` are read when the sheet opens (per client); change the order afterwards with
 * the sheet's `select` / `set`. The hook owns the sheet rather than letting `useOrderSheet` open
 * it, so an unmount while a submit is in flight (the card collapsing, a cancel-first that changes
 * the card) doesn't destroy the sheet between the cancels and the order: it's destroyed when the
 * submit settles.
 */
export function useMmSheet(om: OrderSheetClient, ref: PositionRef, init: SheetInit, canTradeReal: boolean): MmSheet {
  const { format } = useOddsFormat();
  const latest = useRef({ ref, init });
  latest.current = { ref, init };
  const inert = useMemo(() => createInertSheet(om.options, latest.current.ref, latest.current.init), [om]);
  const [opened, setOpened] = useState<{ om: OrderSheetClient; sheet: Sheet } | null>(null);
  const inFlight = useRef<Sheet | null>(null);
  const orphan = useRef<Sheet | null>(null);
  useEffect(() => {
    const sheet = om.openSheet(latest.current.ref, latest.current.init);
    setOpened({ om, sheet });
    return () => {
      setOpened((cur) => (cur?.sheet === sheet ? null : cur));
      if (inFlight.current === sheet) orphan.current = sheet;
      else sheet.destroy();
    };
  }, [om]);
  const h = useOrderSheet(om, opened?.om === om ? opened.sheet : inert);
  const [step, setStep] = useState<MmSheet["step"]>(null);
  const sheetRef = useRef(h.sheet);
  sheetRef.current = h.sheet;
  useEffect(() => setStep(null), [h.sheet]);

  const figures = sheetFigures(h.quote);
  const checking = h.loading || h.refreshing || (h.validation.ok && !!h.quote && !h.preflight && h.preflightStatus !== "error");

  const submit = useCallback<MmSheet["submit"]>(
    async ({ prepare, beforeSend, arm = false } = {}) => {
      const sheet = sheetRef.current;
      inFlight.current = sheet;
      const blocked = (st: SheetState, issues = st.validation.blocking): SubmitOutcome => ({
        status: "blocked",
        message: issueText(issues, st, format, canTradeReal) ?? (st.error ? userMessageFor(st.error) : "This order can't be placed as it stands."),
      });
      try {
        let st = sheet.getState();
        if (st.phase === "unknown") {
          // Re-ask the server about the order already sent (same key): no re-check, no cancels.
          setStep("placing");
          const out = await sheet.place();
          if (out.status === "placed") return { status: "placed", result: out.result, figures: sheetFigures(st.quote), state: sheet.getState() };
          return blocked(sheet.getState(), out.issues);
        }
        setStep("checking");
        const before = sheetFigures(st.quote);
        prepare?.(sheet);
        await sheet.refresh();
        st = sheet.getState();
        if (st.validation.ok && st.quote && !st.preflight) {
          await sheet.preflight();
          st = sheet.getState();
        }
        const now = sheetFigures(st.quote);
        if (!now || figuresMoved(before, now)) return { status: "moved", from: before, to: now };
        if (!st.validation.ok) return blocked(st);

        const real = st.mode === "real";
        if (real && st.phase !== "confirming") {
          const out = await sheet.place();
          if (out.status === "blocked") return blocked(sheet.getState(), out.issues);
          if (out.status === "placed") return { status: "placed", result: out.result, figures: now, state: sheet.getState() }; // realMoney.confirm off
        }
        if (real && arm) return { status: "armed", figures: now };
        setStep("placing");
        await beforeSend?.();
        const out = real ? await sheet.confirm() : await sheet.place();
        if (out.status === "placed") return { status: "placed", result: out.result, figures: now, state: sheet.getState() };
        if (out.status === "blocked" && out.issues.some((i) => i.code === "confirm_expired")) {
          return { status: "moved", from: now, to: sheetFigures(sheet.getState().quote) };
        }
        if (out.status === "confirm_required") return { status: "armed", figures: now };
        return blocked(sheet.getState(), out.issues);
      } catch (e) {
        if (isOrderSheetError(e)) {
          const unknown = sheet.getState().phase === "unknown";
          return {
            status: "error",
            unknown,
            message: unknown
              ? "No answer from OpenMarkets, so the order may have gone through. Press again to check: it won't place a second order."
              : userMessageFor(e),
          };
        }
        return { status: "error", unknown: false, message: e instanceof Error ? e.message : String(e) };
      } finally {
        inFlight.current = null;
        if (orphan.current === sheet) {
          orphan.current = null;
          sheet.destroy();
        }
        setStep(null);
      }
    },
    [format, canTradeReal],
  );

  return { h, figures, checking, step, submit };
}
